// File: src/components/EventsFiltersAndHeadline/DateFilterComponent.jsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { IoIosArrowBack, IoIosArrowForward, IoIosCalendar } from 'react-icons/io';
import { WiSunrise, WiDaySunny, WiNightClear, WiMoonNew } from 'react-icons/wi';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

/* ===============================
   DATE HELPERS
=============================== */
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const addMonths = (base, n) => {
  const d = new Date(base);
  d.setMonth(d.getMonth() + n);
  return d;
};

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isWithin = (day, start, end) => {
  if (!start || !end) return false;

  const cur = startOfDay(day).getTime();
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();

  return cur > s && cur < e;
};

const buildCalendarGrid = (year, monthIndex) => {
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  const firstWeekday = first.getDay();
  const lastWeekday = last.getDay();

  const days = [];

  for (let i = 0; i < firstWeekday; i++) {
    const d = new Date(first);
    d.setDate(d.getDate() - (firstWeekday - i));
    days.push(d);
  }

  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, monthIndex, d));
  }

  for (let i = lastWeekday; i < 6; i++) {
    const d = new Date(last);
    d.setDate(d.getDate() + (i - lastWeekday + 1));
    days.push(d);
  }

  return days;
};

/* ===============================
   DAY CELL
=============================== */
function DayCell({ day, monthDate, startDate, endDate, onPick, today }) {
  const inMonth =
    day.getFullYear() === monthDate.getFullYear() &&
    day.getMonth() === monthDate.getMonth();

  const isStart = startDate && isSameDay(day, startDate);
  const isEnd = endDate && isSameDay(day, endDate);
  const inRange = isWithin(day, startDate, endDate);
  const isToday = isSameDay(day, today);

  const base = {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
    position: 'relative',
    userSelect: 'none',
    color: inMonth ? '#0b0b0b' : '#9ca3af',
  };

  let bg = 'transparent';
  let color = base.color;
  let border = '1px solid transparent';
  let radius = 6;

  if (inRange) {
    bg = '#f3f4f6';
    color = '#0b0b0b';
    border = '1px solid #e5e7eb';
  }

  if (isStart || isEnd) {
    bg = '#0b0b0b';
    color = '#ffffff';
    border = '1px solid #0b0b0b';
  }

  return (
    <button
      type="button"
      onClick={() => onPick(day)}
      aria-label={day.toDateString()}
      style={{ ...base, background: bg, color, border, borderRadius: radius }}
    >
      <span style={{ fontWeight: isStart || isEnd ? 800 : 600 }}>
        {day.getDate()}
      </span>

      {isToday && !isStart && !isEnd && (
        <span
          style={{
            position: 'absolute',
            bottom: 4,
            width: 4,
            height: 4,
            borderRadius: 999,
            background: '#eee',
          }}
        />
      )}
    </button>
  );
}

/* ===============================
   CALENDAR
=============================== */
function Calendar({ startDate, endDate, onChangeRange }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const today = useMemo(() => new Date(), []);
  const base = new Date(today.getFullYear(), today.getMonth(), 1);
  const currentMonthDate = addMonths(base, monthOffset);
  const days = buildCalendarGrid(
    currentMonthDate.getFullYear(),
    currentMonthDate.getMonth()
  );

  const pick = (day) => {
    if (!startDate || (startDate && endDate)) {
      onChangeRange({ start: startOfDay(day), end: null });
    } else {
      const s = startOfDay(startDate);
      const d = startOfDay(day);

      if (d.getTime() < s.getTime()) {
        onChangeRange({ start: d, end: s });
      } else {
        onChangeRange({ start: s, end: d });
      }
    }
  };

  const headerBtn = {
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    width: 28,
    height: 28,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    appearance: 'none',
    WebkitAppearance: 'none',
    padding: 0,
    lineHeight: 0,
    boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '8px 12px 16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          margin: '12px 0',
        }}
      >
        <button
          type="button"
          onClick={() => setMonthOffset((v) => v - 1)}
          style={headerBtn}
          aria-label="Previous month"
        >
          <IoIosArrowBack
            style={{ width: 12, height: 12, display: 'block', color: '#000' }}
          />
        </button>

        <div style={{ fontWeight: 800, fontSize: 14 }}>
          {currentMonthDate.toLocaleString('default', { month: 'long' })}{' '}
          {currentMonthDate.getFullYear()}
        </div>

        <button
          type="button"
          onClick={() => setMonthOffset((v) => v + 1)}
          style={headerBtn}
          aria-label="Next month"
        >
          <IoIosArrowForward
            style={{ width: 12, height: 12, display: 'block', color: '#000' }}
          />
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 8,
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 700,
          marginBottom: 8,
          padding: '0 2px',
        }}
      >
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, index) => (
          <div key={`${d}-${index}`} style={{ textAlign: 'center' }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {days.map((d) => (
          <DayCell
            key={d.toISOString()}
            day={d}
            monthDate={currentMonthDate}
            startDate={startDate}
            endDate={endDate}
            onPick={pick}
            today={today}
          />
        ))}
      </div>
    </div>
  );
}

/* ===============================
   MAIN FILTER SHEET
=============================== */
export default function EventFilter({
  range,
  onChange,
  dateOpen,
  setDateOpen,
}) {
  const [committedStart, setCommittedStart] = useState(null);
  const [committedEnd, setCommittedEnd] = useState(null);
  const [committedTimes, setCommittedTimes] = useState([]);

  const [tempStart, setTempStart] = useState(null);
  const [tempEnd, setTempEnd] = useState(null);
  const [tempTimes, setTempTimes] = useState([]);

  const overlayLockRef = useRef({ didLock: false, y: 0, prevTheme: '' });
  const sheetRef = useRef(null);

  useEffect(() => {
    const item = Array.isArray(range) && range.length ? range[0] : null;

    const nextStart = item?.startDate || null;
    const nextEnd = item?.endDate || null;
    const nextTimes = Array.isArray(item?.times) ? item.times : [];

    setCommittedStart(nextStart);
    setCommittedEnd(nextEnd);
    setCommittedTimes(nextTimes);

    if (!dateOpen) {
      setTempStart(nextStart);
      setTempEnd(nextEnd);
      setTempTimes(nextTimes);
    }
  }, [range, dateOpen]);

  useEffect(() => {
    if (dateOpen) {
      setTempStart(committedStart);
      setTempEnd(committedEnd);
      setTempTimes(committedTimes);
    }
  }, [dateOpen, committedStart, committedEnd, committedTimes]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = meta?.content;

    if (dateOpen) {
      const alreadyLocked =
        document.body.classList.contains('overlay-active') ||
        document.body.style.position === 'fixed';

      overlayLockRef.current.didLock = !alreadyLocked;
      overlayLockRef.current.prevTheme = prevTheme || '#ffffff';

      if (meta) meta.content = '#999998';

      if (!alreadyLocked) {
        const y = window.scrollY || window.pageYOffset || 0;
        overlayLockRef.current.y = y;

        document.body.style.top = `-${y}px`;
        document.body.classList.add('overlay-active');
      }
    }

    return () => {
      if (meta) meta.content = overlayLockRef.current.prevTheme || '#ffffff';

      if (overlayLockRef.current.didLock) {
        const y = overlayLockRef.current.y || 0;

        document.body.classList.remove('overlay-active');
        document.body.style.top = '';
        window.scrollTo(0, y);
      }
    };
  }, [dateOpen]);

  const timeOptions = [
    {
      label: <span style={{ color: '#111827' }}>Morning</span>,
      value: 'morning',
      icon: <WiSunrise style={{ fontSize: 24, color: '#111827' }} />,
      range: '6am–12pm',
    },
    {
      label: <span style={{ color: '#111827' }}>Afternoon</span>,
      value: 'afternoon',
      icon: <WiDaySunny style={{ fontSize: 24, color: '#111827' }} />,
      range: '12pm–5pm',
    },
    {
      label: <span style={{ color: '#111827' }}>Evening</span>,
      value: 'evening',
      icon: <WiNightClear style={{ fontSize: 24, color: '#111827' }} />,
      range: '5pm–10pm',
    },
    {
      label: <span style={{ color: '#111827' }}>Late Night</span>,
      value: 'latenight',
      icon: <WiMoonNew style={{ fontSize: 24, color: '#111827' }} />,
      range: '10pm–6am',
    },
  ];

  const handleApply = () => {
    setCommittedStart(tempStart);
    setCommittedEnd(tempEnd);
    setCommittedTimes(tempTimes);

    onChange?.([
      {
        startDate: tempStart,
        endDate: tempEnd,
        key: 'selection',
        times: tempTimes,
      },
    ]);

    setDateOpen(false);
  };

  const handleCancel = () => {
    setTempStart(committedStart);
    setTempEnd(committedEnd);
    setTempTimes(committedTimes);
    setDateOpen(false);
  };

  const handleClear = () => {
    setTempStart(null);
    setTempEnd(null);
    setTempTimes([]);

    setCommittedStart(null);
    setCommittedEnd(null);
    setCommittedTimes([]);

    onChange?.([
      {
        startDate: null,
        endDate: null,
        key: 'selection',
        times: [],
      },
    ]);

    setDateOpen(false);
  };

  const toggleTime = (value) => {
    setTempTimes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const rangeText = () => {
    const fmt = (d) =>
      d?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

    if (tempStart && tempEnd) return `${fmt(tempStart)} – ${fmt(tempEnd)}`;
    if (tempStart) return `${fmt(tempStart)} – Select end`;
    if (tempEnd) return `Select start – ${fmt(tempEnd)}`;

    return 'Any date';
  };

  return (
    <div
      style={{
        position: 'relative',
        fontFamily:
          "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      {dateOpen && (
        <div
          onClick={() => setDateOpen(false)}
          style={S.backdrop}
          role="presentation"
        >
          <div
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Filter by date"
            style={{
              ...S.sheet,
              paddingBottom: `calc(12px + env(safe-area-inset-bottom, 0px) + ${
                isPWA() ? 8 : 0
              }px)`,
            }}
          >
            <div style={S.header}>
              <div style={S.headerTitle}>Filter by Date</div>

              <button type="button" onClick={handleClear} style={S.resetBtn}>
                Clear
              </button>
            </div>

            <div style={S.body}>
              <div style={S.section}>
                <div style={S.subLabel}>Date range</div>

                <div style={S.rangeRow}>
                  <div style={S.rangeBadge}>
                    <IoIosCalendar style={{ marginRight: 6 }} />
                    <span style={{ fontWeight: 700 }}>{rangeText()}</span>
                  </div>

                  <div style={S.quickRow}>
                    {[
                      {
                        label: 'Today',
                        run: () => {
                          const t = new Date();
                          const s = startOfDay(t);
                          setTempStart(s);
                          setTempEnd(s);
                        },
                      },
                      {
                        label: 'This weekend',
                        run: () => {
                          const t = new Date();
                          const day = t.getDay();
                          const sat = new Date(t);
                          sat.setDate(t.getDate() + ((6 - day + 7) % 7));
                          const sun = new Date(sat);
                          sun.setDate(sat.getDate() + 1);
                          setTempStart(startOfDay(sat));
                          setTempEnd(startOfDay(sun));
                        },
                      },
                      {
                        label: 'Next 7 days',
                        run: () => {
                          const s = startOfDay(new Date());
                          const e = new Date(s);
                          e.setDate(e.getDate() + 6);
                          setTempStart(s);
                          setTempEnd(e);
                        },
                      },
                    ].map((q) => (
                      <button
                        key={q.label}
                        type="button"
                        onClick={q.run}
                        style={S.quickBtn}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ ...S.section, paddingTop: 8 }}>
                <Calendar
                  startDate={tempStart}
                  endDate={tempEnd}
                  onChangeRange={({ start, end }) => {
                    setTempStart(start);
                    setTempEnd(end);
                  }}
                />
              </div>

              <div style={S.section}>
                <div style={S.subLabel}>Time of day</div>

                <div style={S.timeGrid}>
                  {timeOptions.map((opt) => {
                    const active = tempTimes.includes(opt.value);

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleTime(opt.value)}
                        aria-pressed={active}
                        style={{
                          ...S.timeCard,
                          borderColor: active ? '#0b0b0b' : '#eef0f2',
                          background: active ? '#fafafa' : '#ffffff',
                        }}
                      >
                        {opt.icon}

                        <div style={{ marginLeft: 10, textAlign: 'left' }}>
                          <div style={{ fontWeight: 800, fontSize: 13 }}>
                            {opt.label}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {opt.range}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.footer}>
              <button type="button" onClick={handleCancel} style={S.secondaryBtn}>
                Cancel
              </button>

              <button type="button" onClick={handleApply} style={S.primaryBtn}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(16px);
            opacity: 0;
          }

          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation: none !important;
            transition: none !important;
          }
        }

        button:focus-visible {
          outline: 0;
          box-shadow: 0 0 0 3px rgba(59,130,246,.45);
        }
      `}</style>
    </div>
  );
}

/* ===============================
         STYLE TOKENS
=============================== */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  sheet: {
    width: '100%',
    maxWidth: 520,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .25s ease-out',
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 16px',
    borderBottom: '1px solid #eef0f2',
  },

  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },

  resetBtn: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#374151',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },

  body: {
    maxHeight: 'min(64vh, 560px)',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '8px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  section: {
    background: '#fff',
  },

  subLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: 700,
    marginBottom: 6,
  },

  rangeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },

  rangeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #eef0f2',
    background: '#fafafa',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 13,
  },

  quickRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },

  quickBtn: {
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#0b0b0b',
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },

  timeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
    gap: 8,
  },

  timeCard: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    border: '1px solid #eef0f2',
    borderRadius: 10,
    background: '#ffffff',
    cursor: 'pointer',
  },

  timeCardActive: {
    borderColor: '#0b0b0b',
    background: '#fafafa',
  },

  footer: {
    padding: 12,
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 8,
  },

  secondaryBtn: {
    flex: 1,
    background: '#ffffff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },

  primaryBtn: {
    flex: 1,
    background: '#ddd',
    color: '#000',
    border: '1px solid #ddd',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};